import { memo, useCallback, useMemo } from "react";
import { generatePath, Link } from "react-router";

import type { SnackListForDesignPlainQuery } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog";

type Props = {
	hotelID: string;
	snacks: SnackListForDesignPlainQuery["snackList"]["snacks"];
	open?: boolean;
	onAddPlainWidget: (contentID: string, title: string) => void;

	onOpenChange: (open: boolean) => void;
};

/**
 *
 * @todo nuxt の design component の追加が完了したタイミングで、スナック作成の導線を追加する
 *
 */
export const AddSnackDialog = memo(({ snacks, hotelID, open, onOpenChange, onAddPlainWidget }: Props) => {
	const handleClickSnack = useCallback(
		(contentID: string, title: string) => () => {
			// スナックの追加
			onAddPlainWidget(contentID, title);
			// ダイアログを閉じる
			onOpenChange(false);
		},
		[onAddPlainWidget, onOpenChange],
	);

	const snackExists = useMemo(() => {
		return snacks.length > 0;
	}, [snacks]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="md:max-w-4xl">
				<DialogHeader>
					<DialogTitle>スナック一覧</DialogTitle>
				</DialogHeader>

				<div className="border-b border-zinc-300" />

				{snackExists ? (
					<div className="flex flex-wrap gap-4">
						{snacks.map((snack, index) => {
							const handleClick = handleClickSnack(snack.id, snack.title);
							return (
								<div
									key={index}
									className="max-h-32 min-h-32 min-w-32 max-w-32 cursor-pointer"
									onClick={handleClick}
								>
									<img
										src={snack.thumbnail}
										className="h-full w-full rounded-md bg-cover bg-center bg-no-repeat shadow hover:opacity-50"
									/>
									<p className="line-clamp-1 text-xs text-caption-50">{snack.title}</p>
								</div>
							);
						})}
					</div>
				) : (
					<div className="space-y-2">
						<p>スナックがまだ作成されていません</p>

						<div className="flex items-center gap-x-1">
							<p>スナックを作成する場合は</p>
							<Link
								to={generatePath(PATHS.HOTEL.RESERVATION_PAGE.SNACK.LIST, { hotelID })}
								target="_blank"
								className="text-link-400 underline-offset-4 hover:underline"
							>
								こちら
							</Link>
							<p>から</p>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
});
